import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, FileUp, Megaphone, Mail, PenLine } from "lucide-react";
import { PageIntro, Reveal } from "@/components/Reveal";
import { usePageTitle } from "@/lib/hooks";

export const CONTACT_EMAIL = "pricehub26@gmail.com";
const hasEmail = CONTACT_EMAIL.includes("@") && !CONTACT_EMAIL.startsWith("[");

const REASONS = [
  { icon: Megaphone, title: "Promote your brand", text: "Put your brand in front of contractors, dealers and buyers searching for electrical price lists every day.", subject: "Promote my brand on PRICEHUB" },
  { icon: FileUp, title: "Publish official price lists", text: "Send us your latest authorised PDF or Excel price lists and we'll publish them on your brand page.", subject: "Publish our price lists on PRICEHUB" },
  { icon: PenLine, title: "Corrections & removals", text: "Spotted an outdated document or need a listing changed or removed? Let us know and we'll act on it.", subject: "Listing correction request" },
];

const mailto = (subject) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;

export default function ContactPage() {
  usePageTitle("Contact Us");
  return (
    <>
      <PageIntro eyebrow="Contact" title={"LET'S PUT YOUR\nBRAND ON THE MAP"} desc="PRICEHUB is where the electrical trade comes to find price lists. Brand owners and authorised distributors — talk to us about promoting your brand and publishing your official price lists." />
      <section className="mx-auto max-w-7xl px-5 pb-32 md:px-8" data-testid="contact-page">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] bg-[#101625] p-8 text-white ring-1 ring-white/10 md:p-12">
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#7388FF]/30 blur-[90px]" />
            <div className="absolute -bottom-20 left-10 h-56 w-56 rounded-full bg-[#B6FF5C]/15 blur-[90px]" />
            <div className="relative flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="flex items-center gap-2 font-mono-ph text-xs uppercase tracking-[0.2em] text-[#B6FF5C]"><Mail className="h-4 w-4 text-[#B6FF5C]" /> Write to us</p>
                <p className="mt-4 break-words font-display text-[1.6rem] font-extrabold tracking-tight sm:text-5xl lg:text-6xl" data-testid="contact-email">{CONTACT_EMAIL}</p>
                <p className="mt-3 max-w-xl text-sm text-slate-300">We reply to brand and distributor enquiries as quickly as we can.</p>
              </div>
              {hasEmail && (
                <motion.a whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} href={mailto("Promote my brand on PRICEHUB")} data-testid="contact-email-btn"
                  className="inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-[#B6FF5C] px-7 py-4 font-semibold text-[#101625] hover:shadow-[0_0_30px_rgba(182,255,92,0.5)] md:self-auto">
                  Email PRICEHUB <ArrowUpRight className="h-5 w-5" />
                </motion.a>
              )}
            </div>
          </div>
        </Reveal>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {REASONS.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.08}>
              <a href={hasEmail ? mailto(r.subject) : undefined} data-testid={`contact-reason-${i}`}
                className="group flex h-full flex-col rounded-[1.75rem] border border-ph-line bg-ph-surface p-7 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1 hover:border-ph-accent/40 hover:shadow-[0_26px_50px_-28px_rgb(var(--ph-glow)/0.45)]">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ph-raised text-ph-accent transition-[transform,background-color,color] duration-500 group-hover:rotate-[-8deg] group-hover:bg-ph-lime group-hover:text-ph-navy"><r.icon className="h-5 w-5" /></span>
                <p className="mt-6 font-display text-2xl font-bold tracking-tight text-ph-ink">{r.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-ph-muted">{r.text}</p>
                {hasEmail && <span className="mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-semibold text-ph-accent">Start an email <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" /></span>}
              </a>
            </Reveal>
          ))}
        </div>
        <p className="mt-10 text-sm text-ph-muted">Only documents that are authorised for distribution or otherwise lawfully shared are published. <Link to="/legal/disclaimer" className="font-semibold text-ph-accent hover:underline" data-testid="contact-disclaimer-link">Read the disclaimer</Link></p>
      </section>
    </>
  );
}
